import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Always-loaded components (small + critical)
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Sidebar from "./components/Sidebar";
import SetupWizard from "./components/SetupWizard";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";

// Lazy-load heavy components for faster initial page load
const OrderManagement = lazy(() => import("./components/OrderManagement"));
const CustomerManagement = lazy(() => import("./components/CustomerManagement"));
const SupplierManagement = lazy(() => import("./components/SupplierManagement"));
const AccountingManagement = lazy(() => import("./components/AccountingManagement"));
const PayrollManagement = lazy(() => import("./components/PayrollManagement"));
const EnhancedDocumentGeneration = lazy(() => import("./components/EnhancedDocumentGeneration"));
const EnhancedAdminPanel = lazy(() => import("./components/EnhancedAdminPanel"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const errorMessage = error.response?.data?.detail || 'Session expired';
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Show toast message before redirecting
      if (window.location.pathname !== '/' && !window.location.pathname.startsWith('/reset-password')) {
        toast.error(errorMessage + '. Redirecting to login...');
        setTimeout(() => { window.location.href = '/'; }, 1200);
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [view, setView] = useState('login'); // 'login' | 'forgot'
  const [isSetupNeeded, setIsSetupNeeded] = useState(false);

  // Detect reset-password route via URL (independent of React Router since existing app has none)
  const isResetPasswordRoute = window.location.pathname.startsWith('/reset-password');

  useEffect(() => {
    const bootstrap = async () => {
      try {
        // Check setup status from backend
        const res = await axios.get(`${API}/setup/status`);
        setIsSetupNeeded(!res.data.is_setup);
      } catch (e) {
        // If status call fails, don't block login
        setIsSetupNeeded(false);
      }

      // Set browser tab title based on business settings (public fetch)
      try {
        const s = await axios.get(`${API}/public/business-info`);
        if (s.data?.company_name) document.title = s.data.company_name;
      } catch (e) { /* ignore */ }

      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        try { setUser(JSON.parse(userData)); }
        catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    bootstrap();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsSetupNeeded(false);
    toast.success('Login successful!');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setCurrentPage('dashboard');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Reset password page (standalone, no auth required)
  if (isResetPasswordRoute) {
    return (
      <>
        <ResetPassword />
        <Toaster position="top-right" duration={3000} />
      </>
    );
  }

  // Setup wizard (when no users exist)
  if (isSetupNeeded && !user) {
    return (
      <>
        <SetupWizard onSetupComplete={handleLogin} />
        <Toaster position="top-right" duration={3000} />
      </>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {view === 'forgot' ? (
          <ForgotPassword onBack={() => setView('login')} />
        ) : (
          <Login onLogin={handleLogin} apiClient={apiClient} onForgotPassword={() => setView('forgot')} />
        )}
        <Toaster position="top-right" duration={3000} />
      </div>
    );
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard apiClient={apiClient} user={user} />;
      case 'orders':
        return <OrderManagement apiClient={apiClient} user={user} />;
      case 'customers':
        return <CustomerManagement apiClient={apiClient} user={user} />;
      case 'suppliers':
        return <SupplierManagement apiClient={apiClient} user={user} />;
      case 'accounting':
        return <AccountingManagement apiClient={apiClient} user={user} />;
      case 'payroll':
        return <PayrollManagement apiClient={apiClient} user={user} />;
      case 'documents':
        return <EnhancedDocumentGeneration apiClient={apiClient} user={user} />;
      case 'admin':
        return <EnhancedAdminPanel apiClient={apiClient} user={user} />;
      default:
        return <Dashboard apiClient={apiClient} user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="flex">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          user={user}
          onLogout={handleLogout}
        />
        <div className="flex-1 ml-0 lg:ml-64">
          <div className="p-4 sm:p-6 lg:p-8">
            <Suspense fallback={
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            }>
              {renderCurrentPage()}
            </Suspense>
          </div>
        </div>
      </div>
      <Toaster position="top-right" duration={3000} />
    </div>
  );
}

export default App;
